import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/_atoms/select";

import { DatatableRowData } from "../datatable-types";
import { buildColumnDisplayValueComparator } from "../datatable-utils/build-column-display-value-comparator";
import { useDatatableContext } from "../use-datatable-context";

import { useColumnDisplayValue } from "./use-column-display-value";

const NO_SUBSECTION_OPTION = "__NO_SUBSECTION__";

export function DatatableDisplayOptionsSubsectioningSelect<TData extends DatatableRowData>() {
  const { t } = useTranslation("common");
  const { table, getConfigurationForColumn, setState, state, lock } = useDatatableContext<TData>();
  const { sectionColumnId, subSectionColumnId } = state;

  const getDisplayValueForColumn = useColumnDisplayValue<TData>();

  const subSectioningColumnList = useMemo(() => {
    return table
      .getAllColumns()
      .filter(
        (column) =>
          column.id !== sectionColumnId &&
          getConfigurationForColumn(column.id)?.enableSectioning === true &&
          lock.canOfferColumn(column),
      )
      .sort(buildColumnDisplayValueComparator(getDisplayValueForColumn));
  }, [table, getDisplayValueForColumn, getConfigurationForColumn, sectionColumnId, lock]);

  const handleValueChange = (columnId: string) => {
    if (columnId === NO_SUBSECTION_OPTION) {
      setState({
        state: {
          subSectionColumnId: null,
        },
      });
    } else {
      setState({
        state: {
          subSectionColumnId: columnId,
        },
      });
    }
  };

  if (subSectioningColumnList.length === 0) {
    return <></>;
  }

  return (
    <div className="my-2 flex flex-row items-center">
      <div className="mb-2 mr-4 w-44 whitespace-nowrap text-xs font-medium text-muted-foreground">{t("view.subGrouping")}</div>
      <div className="flex w-full flex-row items-center gap-1">
        <Select value={subSectionColumnId ?? ""} onValueChange={handleValueChange}>
          <SelectTrigger className="h-6 px-2 text-xs ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-focus">
            <div className="flex flex-row items-center justify-between text-xs">
              {!subSectionColumnId && t("view.clickToGroup")}
              {subSectionColumnId && <>{getDisplayValueForColumn(subSectionColumnId)}</>}
            </div>
          </SelectTrigger>
          <SelectContent className="z-10000 p-0">
            {subSectioningColumnList.map((column) => {
              return (
                <SelectItem
                  key={column.id}
                  value={column.id}
                  className="flex h-6 flex-row items-center justify-between text-xs"
                >
                  {getDisplayValueForColumn(column.id)}
                </SelectItem>
              );
            })}
            <SelectItem value={NO_SUBSECTION_OPTION} className="flex h-6 flex-row items-center justify-between text-xs">
              {t("view.noGrouping")}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
